import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";
import { createAppQueryClient } from "./api/queryClient";
import { appRoutes } from "./App";
import { followDeviceTheme } from "./lib/theme";
import "@convenor/design-system/tokens.css";
import "@convenor/design-system/base.css";
import "@convenor/design-system/components.css";
import "./styles/base.css";
// The app's own sheets still waiting for their screens to move (see the files):
import "./ui/Layout.css";
import "./ui/StatusBadge.css";

followDeviceTheme();

const queryClient = createAppQueryClient();

// A data router, so pages can warn before in-app navigation drops unsaved
// changes (useBlocker).
const router = createBrowserRouter(appRoutes);

// Leaving a page, the browser fades the old page out while the new one comes in (a view transition;
// the design system's page enter fades the new page's blocks in, a beat apart; Edgar, 5 Nov 2026).
// The sidebar and the top bar keep still. Only when going to another page: not for a filter or a
// choice within the page (only the query changes), nor a redirect (replace), nor Back and Forward,
// nor for people who ask for less motion, nor in a browser without view transitions.
const navigateNow = router.navigate.bind(router);
router.navigate = ((to: Parameters<typeof navigateNow>[0], opts?: Parameters<typeof navigateNow>[1]) => {
  const fade =
    typeof to !== "number" &&
    !opts?.replace &&
    "startViewTransition" in document &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
    toOtherPage(to);
  return fade ? navigateNow(to as never, { viewTransition: true, ...opts }) : navigateNow(to as never, opts);
}) as typeof router.navigate;

/** Whether a navigation leaves the page: another path, not only another query or fragment. */
function toOtherPage(to: unknown): boolean {
  const path = typeof to === "string" ? to : to && typeof to === "object" ? ((to as { pathname?: string }).pathname ?? "") : "";
  if (!path) return false;
  return new URL(path, window.location.href).pathname !== window.location.pathname;
}

// A page fade that a quick second navigation interrupts is skipped; the browser reports that as an
// error nobody can act on ("Transition was skipped"), so it is not reported.
window.addEventListener("unhandledrejection", (e) => {
  const r = e.reason as { name?: string; message?: string } | undefined;
  if (r?.name === "AbortError" && /transition was skipped/i.test(r.message ?? "")) e.preventDefault();
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
